// src/contexts/SchoolProgramsContext.jsx
//
// Mirrors SupplierProgramsContext.jsx's shape/pattern exactly (same
// apiFetch helper, same auto-fetch-on-login-ready effect, same
// showAlert usage) so it drops into the app the same way — just
// pointed at the /api/school/programs/* endpoints instead.
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useUser } from './UserContext';

const ENV_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  '';
const CLEAN_ENV_BASE = ENV_BASE.replace(/\/+$/, '');
const FALLBACK_BASE = 'https://www.doroosonline.co.il';
const API_BASE = CLEAN_ENV_BASE || FALLBACK_BASE;
const url = (path) => `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

const DEFAULT_TIMEOUT_MS = 12000;

const getStoredToken = () => {
  try {
    return localStorage.getItem('jwt') ?? localStorage.getItem('token') ?? null;
  } catch {
    return null;
  }
};

async function parseJSONSafely(res) {
  if (res.status === 204) return {};
  const text = await res.text().catch(() => '');
  if (!text) return {};
  try { return JSON.parse(text); } catch { return { raw: text }; }
}

async function apiFetch(path, { method = 'GET', body, timeout = DEFAULT_TIMEOUT_MS } = {}) {
  const token = getStoredToken();
  const hasBody = body !== undefined;

  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch(url(path), {
      method,
      headers: {
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: hasBody ? JSON.stringify(body) : undefined,
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    });

    const data = await parseJSONSafely(res);

    if (!res.ok) {
      const msg =
        data?.error ||
        (typeof data?.error === 'object' ? Object.values(data.error)[0] : null) ||
        (res.status === 401 ? 'שגיאה בהרשאות (401)' : `שגיאת שרת (${res.status})`);
      throw new Error(msg);
    }

    return data ?? {};
  } catch (e) {
    if (e.name === 'AbortError') throw new Error('זמן ההמתנה לשרת הסתיים. נסה שוב.');
    if (e instanceof TypeError) {
      throw new Error(`שגיאת רשת: לא ניתן להגיע לשרת ב- ${API_BASE}.`);
    }
    throw e;
  } finally {
    clearTimeout(t);
  }
}

const SchoolProgramsContext = createContext(null);

export const SchoolProgramsProvider = ({ children }) => {
  const { role, userType, showAlert, ready, isLogedin } = useUser();
  const isSchool = role === 'school' || userType === 'school';

  const [myPrograms, setMyPrograms] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchMyPrograms = useCallback(async () => {
    if (!isSchool) return [];
    setLoading(true);
    try {
      const data = await apiFetch('/api/school/programs');
      const items = Array.isArray(data?.items) ? data.items : [];
      setMyPrograms(items);
      setLoaded(true);
      return items;
    } catch (err) {
      showAlert?.(err.message || 'שגיאה בטעינת תוכניות בית הספר', 'warning');
      return [];
    } finally {
      setLoading(false);
    }
  }, [isSchool, showAlert]);

  const fetchCatalog = useCallback(async () => {
    if (!isSchool) return [];
    try {
      const data = await apiFetch('/api/school/programs/catalog');
      const items = Array.isArray(data?.items) ? data.items : [];
      setCatalog(items);
      return items;
    } catch (err) {
      showAlert?.(err.message || 'שגיאה בטעינת תוכניות זמינות להצטרפות', 'warning');
      return [];
    }
  }, [isSchool, showAlert]);

  const joinProgram = useCallback(async (programId) => {
    const data = await apiFetch('/api/school/programs/join', {
      method: 'POST',
      body: { program_id: programId },
    });
    const created = data?.schoolProgram;
    if (created) {
      setMyPrograms((prev) => [created, ...prev]);
      setCatalog((prev) => prev.filter((p) => p.id !== programId));
    }
    return created;
  }, []);

  const fetchStats = useCallback(async (schoolProgramId) => {
    const data = await apiFetch(`/api/school/programs/${schoolProgramId}/stats`);
    return data;
  }, []);

  // Dashboard-only aggregate: totals + combined and per-program
  // monthly trends across every joined program, in one call.
  const fetchDashboardTrends = useCallback(async () => {
    if (!isSchool) return null;
    const data = await apiFetch('/api/school/dashboard/trends');
    return data;
  }, [isSchool]);

  const fetchCoupons = useCallback(async (schoolProgramId, status) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const data = await apiFetch(`/api/school/programs/${schoolProgramId}/coupons${qs}`);
    return Array.isArray(data?.items) ? data.items : [];
  }, []);

  // Starts a real-money budget top-up. For card/paypal this returns a
  // paymentUrl/approvalUrl meant to be opened in a popup — the popup
  // eventually posts {action:'close', status, payload} back via
  // window.postMessage (see PurchaseBudgetModal.jsx), at which point
  // the caller should re-fetch this program. For bank_transfer it
  // returns bank details + a reference code immediately; the budget
  // only updates once an admin reconciles it (fetchMyPrograms() again
  // later will pick that up).
  const purchaseBudget = useCallback(async (schoolProgramId, { amount, method }) => {
    // For card/paypal, the server requires the school's profile phone
    // to already be verified (see PhoneVerificationController) — if
    // it isn't, this throws with a Hebrew message telling the school
    // to verify their phone in their profile first.
    const data = await apiFetch(`/api/school/programs/${schoolProgramId}/budget/purchase`, {
      method: 'POST',
      body: { amount, method },
    });
    return data;
  }, []);

  // Uploads the bank's transfer-confirmation PDF for a pending
  // bank_transfer budget purchase (purchaseId comes from
  // purchaseBudget()'s bank_transfer result). Multipart, so this
  // bypasses apiFetch (which always JSON-encodes) and builds the
  // request by hand — same token/base-URL/timeout conventions.
  const uploadBudgetReceipt = useCallback(async (purchaseId, file) => {
    const token = getStoredToken();
    const form = new FormData();
    form.append('receipt', file);

    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 20000); // PDFs can take longer than the default 12s
    try {
      const res = await fetch(url(`/api/school/programs/budget-purchases/${purchaseId}/receipt`), {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: form,
        mode: 'cors',
        cache: 'no-store',
        credentials: 'omit',
        signal: controller.signal,
      });
      const data = await parseJSONSafely(res);
      if (!res.ok) {
        const msg =
          data?.error ||
          (typeof data?.error === 'object' ? Object.values(data.error)[0] : null) ||
          `שגיאת שרת (${res.status})`;
        throw new Error(msg);
      }
      return data ?? {};
    } catch (e) {
      if (e.name === 'AbortError') throw new Error('זמן ההעלאה הסתיים. נסה שוב.');
      if (e instanceof TypeError) throw new Error(`שגיאת רשת: לא ניתן להגיע לשרת ב- ${API_BASE}.`);
      throw e;
    } finally {
      clearTimeout(t);
    }
  }, []);

  const issueCoupons = useCallback(async (schoolProgramId, payload) => {
    const data = await apiFetch(`/api/school/programs/${schoolProgramId}/coupons`, {
      method: 'POST',
      body: payload,
    });
    // Budget was just consumed server-side — refresh this program's
    // numbers so the card's remaining-budget figure stays correct
    // without a full page reload.
    try {
      const fresh = await apiFetch(`/api/school/programs/${schoolProgramId}`);
      if (fresh?.schoolProgram) {
        setMyPrograms((prev) =>
          prev.map((p) => (p.id === schoolProgramId ? fresh.schoolProgram : p))
        );
      }
    } catch {
      // Non-fatal — the coupons were issued either way; the card will
      // just be stale until the next manual refresh.
    }
    return data;
  }, []);

  // Marks a coupon batch (one issueCoupons() call) as printed, once
  // the school actually opens the print dialog for it — see
  // IssueCouponsModal.jsx's handlePrintBatch().
  const markCouponBatchPrinted = useCallback(async (schoolProgramId, batchId) => {
    const data = await apiFetch(`/api/school/programs/${schoolProgramId}/coupon-batches/${batchId}/print`, {
      method: 'POST',
    });
    return data;
  }, []);

  useEffect(() => {
    // Same rationale as SupplierProgramsContext: wait for the token to
    // actually be verified (ready) and an active session (isLogedin)
    // before fetching, so a stale localStorage role from a previous
    // account never fires a fetch on the login page.
    if (!ready) return;

    if (!isLogedin) {
      setMyPrograms([]);
      setCatalog([]);
      setLoaded(false);
      return;
    }

    if (!isSchool) return;
    fetchMyPrograms();
  }, [ready, isLogedin, isSchool, fetchMyPrograms]);

  const value = useMemo(
    () => ({
      myPrograms,
      catalog,
      loading,
      loaded,
      fetchMyPrograms,
      fetchCatalog,
      joinProgram,
      fetchStats,
      fetchDashboardTrends,
      fetchCoupons,
      issueCoupons,
      markCouponBatchPrinted,
      purchaseBudget,
      uploadBudgetReceipt,
    }),
    [myPrograms, catalog, loading, loaded, fetchMyPrograms, fetchCatalog, joinProgram, fetchStats, fetchDashboardTrends, fetchCoupons, issueCoupons, markCouponBatchPrinted, purchaseBudget, uploadBudgetReceipt]
  );

  return (
    <SchoolProgramsContext.Provider value={value}>
      {children}
    </SchoolProgramsContext.Provider>
  );
};

export const useSchoolPrograms = () => {
  const ctx = useContext(SchoolProgramsContext);
  if (!ctx) throw new Error('useSchoolPrograms must be used within a SchoolProgramsProvider');
  return ctx;
};